import dayjs from "dayjs"
import type { TFunction } from "i18next"

import type { MilestoneTimelineVsProjectDurationValidation } from "../ContractValidation/calculate-contract-validation.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import type {
  ProposalMilestoneDatesVsProjectDatesValidation,
  ProposalMilestoneDurationVsOwnDatesValidation,
  ProposalMilestoneOutsideExample,
  ProposalProjectDurationVsDatesValidation,
  ProposalSalesPriceValidation,
  ProposalTotalMilestoneDurationVsProjectDurationValidation,
  ProposalValidationResult
} from "./types.ts"

/** One renderable line — shared shape for both the compact card and the
 * Generate-time review modal, so the two can never show different wording
 * or different numbers for the same check. */
interface ValidationLine {
  title: string
  /** Present only for a `warning`/`error` line — the before/after/
   * difference (or example) detail, or (for the combined Schedule line) one
   * entry per distinct issue found. Absent for `incomplete` (a single
   * neutral sentence). */
  comparison?: string | string[]
}

// Shares the Contract card's own `durationDays` leaf — the plural rule for
// "N day(s)" has nothing document-specific about it, so pointing at the
// same key avoids a duplicate i18n leaf.
function formatDurationDays(t: TFunction, days: number): string {
  return t("contractValidation.durationDays", { count: Math.abs(days) })
}

function formatExampleDate(value: string | null): string {
  if (!value) return ""
  const parsed = dayjs(value, "YYYY-MM-DD", true)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : value
}

function formatSalesPriceLine(
  t: TFunction,
  validation: ProposalSalesPriceValidation
): ValidationLine | null {
  if (validation.status === "warning") {
    return {
      title: t("proposalValidation.salesPrice.warningTitle"),
      comparison: t("proposalValidation.salesPrice.comparison", {
        salesPrice: budgetFormat(validation.sales_price),
        milestoneTotal: budgetFormat(validation.milestone_total),
        difference: budgetFormat(
          validation.difference === null ? null : Math.abs(validation.difference)
        )
      })
    }
  }
  if (validation.status === "incomplete") {
    return { title: t("proposalValidation.salesPrice.incompleteData") }
  }
  return null
}

/** Only ever returns non-null for the actual problem state (`warning`) —
 * `valid` and `incomplete` both carry nothing worth surfacing as a Schedule
 * issue (see `formatScheduleLine`, this function's only remaining caller). */
function formatProjectDurationLine(
  t: TFunction,
  validation: ProposalProjectDurationVsDatesValidation
): ValidationLine | null {
  if (validation.status === "warning") {
    return {
      title: t("proposalValidation.projectDuration.warningTitle"),
      comparison: t("proposalValidation.projectDuration.comparison", {
        duration: formatDurationDays(t, validation.duration_days ?? 0),
        dateSpan: formatDurationDays(t, validation.date_span_days ?? 0),
        difference: formatDurationDays(t, validation.difference_days ?? 0)
      })
    }
  }
  return null
}

/** Only ever returns non-null for the actual problem state (`error`) — see
 * `formatProjectDurationLine`'s docstring for why. */
function formatMilestoneDurationLine(
  t: TFunction,
  validation: ProposalMilestoneDurationVsOwnDatesValidation
): ValidationLine | null {
  if (validation.status === "error") {
    return {
      title: t("proposalValidation.milestoneDuration.errorTitle"),
      comparison: t("proposalValidation.milestoneDuration.comparison", {
        count: validation.mismatched_count,
        checked: validation.checked_count
      })
    }
  }
  return null
}

/** Only ever returns non-null for the actual problem state (`warning`) — see
 * `formatProjectDurationLine`'s docstring for why. */
function formatTotalMilestoneDurationLine(
  t: TFunction,
  validation: ProposalTotalMilestoneDurationVsProjectDurationValidation
): ValidationLine | null {
  if (validation.status === "warning") {
    return {
      title: t("proposalValidation.totalMilestoneDuration.warningTitle"),
      comparison: t("proposalValidation.totalMilestoneDuration.comparison", {
        projectDuration: formatDurationDays(t, validation.project_duration_days ?? 0),
        milestoneTotal: formatDurationDays(t, validation.milestone_total_days ?? 0),
        difference: formatDurationDays(t, validation.difference_days ?? 0)
      })
    }
  }
  return null
}

function formatMilestoneDatesExample(
  t: TFunction,
  example: ProposalMilestoneOutsideExample
): string {
  const startsBefore = example.violations.includes("starts_before_project")
  const endsAfter = example.violations.includes("ends_after_project")
  const name = example.name || t("proposalValidation.milestoneDates.unnamedMilestone")

  if (startsBefore && endsAfter) {
    return t("proposalValidation.milestoneDates.startsBeforeAndEndsAfter", {
      name,
      start: formatExampleDate(example.start_date),
      end: formatExampleDate(example.end_date)
    })
  }
  if (startsBefore) {
    return t("proposalValidation.milestoneDates.startsBefore", {
      name,
      start: formatExampleDate(example.start_date)
    })
  }
  return t("proposalValidation.milestoneDates.endsAfter", {
    name,
    end: formatExampleDate(example.end_date)
  })
}

/** Only ever returns non-null for the actual problem state (`error`) — see
 * `formatProjectDurationLine`'s docstring for why. */
function formatMilestoneDatesLine(
  t: TFunction,
  validation: ProposalMilestoneDatesVsProjectDatesValidation
): ValidationLine | null {
  if (validation.status === "error") {
    const [firstExample] = validation.outside_examples
    const comparison =
      validation.outside_count === 1 && firstExample
        ? formatMilestoneDatesExample(t, firstExample)
        : t("proposalValidation.milestoneDates.multipleOutside", {
            count: validation.outside_count
          })
    return { title: t("proposalValidation.milestoneDates.errorTitle"), comparison }
  }
  return null
}

type ScheduleStatus = "warning" | "error" | "incomplete"

/** The combined "Schedule" category: Project Duration vs. Dates, Milestone
 * Duration vs. Own Dates, Total Milestone Duration vs. Project Duration, and
 * Milestone Dates vs. Project Dates, folded into ONE card/modal row instead
 * of four independent ones — the product owner's explicit correction
 * (2026-09-17): exactly two categories, Sales Price and Schedule, nothing
 * more. Mirrors `ContractValidation/format-contract-validation.ts`'s own
 * `formatScheduleLine` exactly — see that function's docstring for the
 * overall-status rollup rule.
 */
function formatScheduleLine(
  t: TFunction,
  result: Pick<
    ProposalValidationResult,
    | "project_duration_vs_dates"
    | "milestone_duration_vs_own_dates"
    | "total_milestone_duration_vs_project_duration"
    | "milestone_dates_vs_project_dates"
  >
): (ValidationLine & { status: ScheduleStatus }) | null {
  const {
    project_duration_vs_dates: proj,
    milestone_duration_vs_own_dates: milestoneDuration,
    total_milestone_duration_vs_project_duration: totalMilestoneDuration,
    milestone_dates_vs_project_dates: milestoneDates
  } = result

  const statuses = [
    proj.status,
    milestoneDuration.status,
    totalMilestoneDuration.status,
    milestoneDates.status
  ]

  const status: ScheduleStatus | "valid" = statuses.includes("error")
    ? "error"
    : statuses.includes("warning")
      ? "warning"
      : statuses.every((s) => s === "incomplete")
        ? "incomplete"
        : "valid"

  if (status === "valid") return null
  if (status === "incomplete") {
    return { title: t("proposalValidation.schedule.incompleteData"), status }
  }

  const issues: string[] = []
  for (const line of [
    formatProjectDurationLine(t, proj),
    formatMilestoneDurationLine(t, milestoneDuration),
    formatTotalMilestoneDurationLine(t, totalMilestoneDuration),
    formatMilestoneDatesLine(t, milestoneDates)
  ]) {
    if (line?.comparison) issues.push(`${line.title}: ${line.comparison}`)
  }

  return { title: t("proposalValidation.schedule.title"), comparison: issues, status }
}

/** The Validation card's "Milestone duration / timeline does not match
 * Project duration" row — the project's own timeline vs. the OVERALL
 * milestone timeline (earliest Target Start through latest Target End, see
 * `calculateMilestoneTimelineVsProjectDuration`). Only ever returns
 * non-null for the actual problem state (`warning`) — hidden when they
 * agree, and silent (not "not enough information") when there's genuinely
 * no usable date on either side, per the product owner's explicit
 * instruction not to nag about missing data here. */
function formatMilestoneTimelineLine(
  t: TFunction,
  validation: MilestoneTimelineVsProjectDurationValidation
): ValidationLine | null {
  if (validation.status !== "warning") return null
  return {
    title: t("proposalValidation.milestoneTimeline.warningTitle"),
    comparison: t("proposalValidation.milestoneTimeline.comparison", {
      project: formatDurationDays(t, validation.project_duration_days ?? 0),
      milestones: formatDurationDays(t, validation.milestone_timeline_days ?? 0),
      difference: formatDurationDays(t, Math.abs(validation.difference_days ?? 0))
    })
  }
}

export type { ValidationLine }
export { formatSalesPriceLine, formatScheduleLine, formatMilestoneTimelineLine }
